/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-29 07:46:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Pagination } from '@shopify/polaris';
import { reduxForm, FieldArray, reset, getFormValues } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import RedirectsOnEdit from 'src/components/views/redirect/redirects-on-edit';
import ActionsGroupOnUpdating from 'src/components/views/redirect/actions-group-on-updating';
import ActionsGroupOnDeleting from 'src/components/views/redirect/actions-group-on-deleting';
import ConfirmRemoveModal from 'src/components/views/redirect/confirm-remove-modal';
import InvalidInputBanner from 'src/components/views/redirect/invalid-input-banner';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { routes } from 'src/routes';
import { NUMBER_REDIRECT_PER_PAGE } from 'src/constants/redirect';
import { confirmLeavePage } from 'src/helpers';
import {
  buildRedirects,
  checkValidateRedirects,
  isEmptyRedirects
} from 'src/helpers/redirect-helpers';
import { StandardSearchTab } from 'src/constants/search'

import * as redirectActions from 'src/stores/actions/redirect-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';

import type { ComposedProps, State } from './edit.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import history from 'src/routes/history';
import { NUMBER_SYNONYMS_PER_PAGE } from 'src/constants/synonym';

class EditRedirect extends Component<ComposedProps, State> {
  state = {
    // Search string
    inputSearchText: '',

    // Show/Hide remove modal
    activeRemoveModal: false,

    // Show/Hide deleting buttons
    activeDeletingButtons: false,

    skip: 0,
    limit: NUMBER_REDIRECT_PER_PAGE,
    hasPrevious: false,
    hasNext: false,

    // List of selected redirects to be deleted
    selectedRedirects: [],
    discardDeletingChange: false,

    // Show/Hide error banner
    dismissedBanner: true,
    messageError: ''
  };

  componentDidMount() {
    // Get list of redirects for selected page
    const queries = new URLSearchParams(window.location.hash);
    let q = queries.get('q') || '';
    let skip = queries.get('skip') || 0;
    let limit = queries.get('limit') || NUMBER_SYNONYMS_PER_PAGE;
    skip = parseInt(skip);
    limit = parseInt(limit);

    this.setState({ skip, limit, inputSearchText: q });
    this.props.getRedirects(q, skip, limit);

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { skip, limit } = this.state;
    const { total, deleting } = nextProps;
    const { deleting: deletingPrev } = this.props;

    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;

    let updatedState = { hasNext, hasPrevious };

    // Push to management page if there's no data
    if (total === 0) {
      this.backToManagementPage();
    }

    // Hide confirm remove modal
    if (deleting === false && deletingPrev === true) {
      updatedState = _.assign(updatedState, {
        activeRemoveModal: false,
        activeDeletingButtons: false,
        selectedRedirects: []
      });
    }

    this.setState(updatedState);
  }

  render() {
    const { i18n, dirty, handleSubmit, pristine, submitting, saving, deleting, formValues } =
      this.props;
    const {
      skip,
      limit,
      activeDeletingButtons,
      discardDeletingChange,
      dismissedBanner,
      messageError,
      hasPrevious,
      hasNext
    } = this.state;

    const emptyRedirects = formValues ? isEmptyRedirects(formValues['redirects']) : true;

    return (
      <div className="BC-Page BC-Page__SuggestionDictionary BC-Page__Redirect BC-Page__Redirect-New">
        <Page
          title={i18n.translate('AdminHTML.Redirects.Edit.title')}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Redirects.Redirects.title'),
              onAction: this.backToManagementPage
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              {activeDeletingButtons ? (
                <ActionsGroupOnDeleting
                  handleDelete={this.handleChangeModal}
                  discardChange={this.discardDeletingChange}
                />
              ) : (
                <ActionsGroupOnUpdating
                  handleSubmit={handleSubmit(this.handleSave.bind(this))}
                  handleCancel={this.backToManagementPage}
                  pristine={pristine}
                  submitting={submitting}
                  saving={saving}
                  emptyRedirects={emptyRedirects}
                />
              )}
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Banner error */}
                {!dismissedBanner && (
                  <InvalidInputBanner onDismiss={this.handleDismissBanner} message={messageError} />
                )}
              </div>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <Card.Section>
                  {/* List of redirect */}
                  <form onSubmit={this.handleDefaultSubmit} className="editRedirect">
                    <FieldArray
                      name="redirects"
                      component={RedirectsOnEdit}
                      skip={skip}
                      limit={limit}
                      handleChangeDeletingButtonGroup={this.handleChangeDeletingButtonGroup}
                      discardDeletingChange={discardDeletingChange}
                      turnOffDiscardDeletingChangeFlag={this.turnOffDiscardDeletingChangeFlag}
                    />
                  </form>
                </Card.Section>
              </Card>

              {/* Pagination*/}
              <div className="pagination">
                {(hasPrevious || hasNext) && (
                  <Pagination
                    hasPrevious={hasPrevious}
                    onPrevious={this.getPreviousPage}
                    hasNext={hasNext}
                    onNext={this.getNextPage}
                  />
                )}

                <div className="actionsGroup">
                  {activeDeletingButtons ? (
                    <ActionsGroupOnDeleting
                      handleDelete={this.handleChangeModal}
                      discardChange={this.discardDeletingChange}
                    />
                  ) : (
                    <ActionsGroupOnUpdating
                      handleSubmit={handleSubmit(this.handleSave.bind(this))}
                      handleCancel={this.backToManagementPage}
                      pristine={pristine}
                      submitting={submitting}
                      saving={saving}
                      emptyRedirects={emptyRedirects}
                    />
                  )}
                </div>
              </div>
            </Layout.Section>
          </Layout>
        </Page>

        <ConfirmRemoveModal
          active={this.state.activeRemoveModal}
          handleChange={this.handleChangeModal}
          handleRemove={this.deleteRedirects}
          title={i18n.translate('AdminHTML.Redirects.Edit.deleteSelected')}
          content={i18n.translate('AdminHTML.Redirects.Edit.confirmDelete')}
          deleting={deleting}
        />

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path =>
            history.push({
              pathname: path,
              hash: StandardSearchTab.redirects
            })
          }
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Back to management redirect
  backToManagementPage = () => {
    history.push({
      pathname: routes.redirect.view()
    });
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_REDIRECT_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    this.props.getRedirects(inputSearchText || '', previousSkip, previousLimit);
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit });
    this.props.getRedirects(inputSearchText || '', nextSkip, NUMBER_REDIRECT_PER_PAGE);
  };

  // Handle show/hide deleting button
  handleChangeDeletingButtonGroup = (status, selectedRedirects) => {
    this.setState({ activeDeletingButtons: status, selectedRedirects });
  };

  // Handle open/close modal delete
  handleChangeModal = () => {
    this.setState(({ activeRemoveModal }) => ({
      activeRemoveModal: !activeRemoveModal
    }));
  };

  // Save redirects
  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  // Save redirects
  async handleSave(data) {
    const { i18n, initialValues } = this.props;
    const { redirects = [] } = data;

    const validateRedirectsResult = checkValidateRedirects(redirects);
    if (!_.isEmpty(validateRedirectsResult)) {
      this.setState({
        dismissedBanner: false,
        messageError: i18n.translate(validateRedirectsResult)
      });
      return;
    }

    this.setState({ dismissedBanner: true, messageError: '' });

    // Save updated redirect
    // Build redirects data before saving
    const updatedRedirects = buildRedirects(redirects);

    mixpanelV3Helper.trackEditRedirectRules(
      buildRedirects(initialValues.redirects),
      updatedRedirects
    );
    await this.props.updateRedirect(updatedRedirects);
    await this.props.getChecklist();

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  }

  // Handle hide banner
  handleDismissBanner = () => {
    // Hide banner
    this.setState({ dismissedBanner: true, messageError: '' });
  };

  // Discard
  discardChange = () => {
    const { resetForm } = this.props;

    // Reset form
    resetForm();
  };

  // Delete selected redirects
  discardDeletingChange = () => {
    this.setState({
      activeDeletingButtons: false,
      selectedRedirects: [],
      discardDeletingChange: true
    });
  };

  turnOffDiscardDeletingChangeFlag = () => {
    this.setState({ discardDeletingChange: false });
  };

  deleteRedirects = async () => {
    const { selectedRedirects } = this.state;
    const { redirects } = this.props;

    // Collect redirect ids
    const redirectIds = _.map(selectedRedirects, item => {
      const { index } = item;
      const redirect = redirects[index] || {};
      return redirect.id;
    });

    // Delete redirect
    const result = await this.props.deleteRedirects(redirectIds);

    // Get redirect for current page
    const { type } = result;
    if (type === 'DELETE_REDIRECTS_SUCCESS') {
      const { skip, limit, inputSearchText } = this.state;

      // Turn off delete mode
      this.turnOffDiscardDeletingChangeFlag();

      // Get redirects
      this.props.getRedirects(inputSearchText || '', skip, limit);

      // Get checklist
      this.props.getChecklist();
    }
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getRedirects: (q, skip, limit) =>
    dispatch(redirectActions.getRedirects(q, skip, limit, ownProps.i18n)),
  updateRedirect: redirects =>
    dispatch(redirectActions.updateRedirect(redirects, undefined, ownProps.i18n)),
  deleteRedirects: termIds => dispatch(redirectActions.deleteRedirects(termIds, ownProps.i18n)),
  resetForm: () => dispatch(reset('EditRedirectsForm')),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const redirects = _.get(state.redirect, 'redirects', []);
  const formValues = getFormValues('EditRedirectsForm')(state);

  return {
    list: _.get(state.redirect, 'redirects', []),
    total: _.get(state.redirect, 'total', 0),
    loading: _.get(state.redirect, 'loading', false),
    saving: _.get(state.redirect, 'saving', false),
    deleting: _.get(state.redirect, 'deleting', false),
    redirects,
    initialValues: { redirects },
    formValues
  };
};

const EditRedirectsForm = reduxForm({
  form: 'EditRedirectsForm', // a unique identifier for this form
  enableReinitialize: true
})(EditRedirect);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(EditRedirectsForm));
